import React, { useCallback, useRef, useState } from 'react';

import AIVoiceFilterMultiToggle from 'js/src/components/app/VoiceSelectionModal/AIVoiceFilterMultiToggle';
import useAIVoiceFilters from 'js/src/components/app/VoiceSelectionModal/useAIVoiceFilters';
import useAIVoiceSearch from 'js/src/components/app/VoiceSelectionModal/useAIVoiceSearch';
import { SecondaryButton } from 'js/src/components/common/ui/Button';
import Popover, { POPOVER_PLACEMENT } from 'js/src/components/common/ui/Popover';
import { pluralize } from 'js/src/libs/custom-utils';

interface AIVoiceBasicFiltersProps {
    className?: string;
}

const AIVoiceBasicFilters: React.FC<AIVoiceBasicFiltersProps> = ({ className }) => {
    const targetRef = useRef<HTMLDivElement | null>(null);

    const [show, setShow] = useState(false);

    const { ageFilterOptions, qualityFilterOptions, genderFilterOptions } = useAIVoiceFilters();
    const {
        selectedAge,
        selectedQuality,
        selectedGender,
        onChangeAge,
        onChangeQuality,
        onChangeGender,
    } = useAIVoiceSearch();

    const togglePopover = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        setShow((prev) => !prev);
    }, []);

    const hidePopover = useCallback(() => {
        setShow(false);
    }, []);

    const showGeneralFilters =
        genderFilterOptions.length || qualityFilterOptions.length || ageFilterOptions.length;
    if (!showGeneralFilters) {
        return null;
    }

    let buttonText = 'Filters';
    const selectedFilters = [selectedAge, selectedQuality, selectedGender].filter(
        (value) => !!value,
    );
    if (selectedFilters.length > 0) {
        buttonText = `${selectedFilters.length} ${pluralize(selectedFilters.length, 'Filter')}`;
    }

    return (
        <div className={className} ref={targetRef}>
            <SecondaryButton text={buttonText} iconLeft="tune" onClick={togglePopover} />
            <Popover
                show={show}
                target={targetRef}
                placement={POPOVER_PLACEMENT.BOTTOM_END}
                onHide={hidePopover}
            >
                <div className="d-flex flex-column gap-2 py-l5-16">
                    {qualityFilterOptions.length > 0 && (
                        <AIVoiceFilterMultiToggle
                            label="Quality"
                            options={qualityFilterOptions}
                            selectedValue={selectedQuality}
                            onSelect={onChangeQuality}
                        />
                    )}
                    {genderFilterOptions.length > 0 && (
                        <AIVoiceFilterMultiToggle
                            label="Gender"
                            options={genderFilterOptions}
                            selectedValue={selectedGender}
                            onSelect={onChangeGender}
                        />
                    )}
                    {ageFilterOptions.length > 0 && (
                        <AIVoiceFilterMultiToggle
                            label="Age"
                            options={ageFilterOptions}
                            selectedValue={selectedAge}
                            onSelect={onChangeAge}
                        />
                    )}
                </div>
            </Popover>
        </div>
    );
};

export default AIVoiceBasicFilters;
